import {
  CheckIcon,
  ChevronUpIcon,
  Cross2Icon,
  ExclamationTriangleIcon,
  QuestionMarkIcon,
} from '@radix-ui/react-icons';
import { Badge, Card, DataList, Flex, Separator, Text } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { JsonViewer } from 'components/common/json/json-viewer';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { RowWithID } from 'interfaces/tables/main';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  HealthCheckStatus,
  IHealthCheckSubmoduleCheckResponse,
} from 'lib_ts/interfaces/i-machine-health-check';
import {
  IHealthCheckSubmoduleCheckResult,
  IHealthCheckSubmoduleResults,
} from 'lib_ts/interfaces/machine-msg/i-health-check';
import { startCase } from 'lodash';
import React, { useContext, useMemo, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import './index.scss';

/**
 Example row:
  {
    _id: 'TiltController',
    status: 'pass',
    name: 'TiltController',
    results: [
      {
        status: 'pass',
        message: 'All IPs reachable.',
        check_type: 'ip_all_hosts_reachable',
        failure_reasons: [],
        event_id: 'misc-no-internet-connection',
        device: "Network"
      },
    ],
  }
*/

const getTestColor = (status: HealthCheckStatus) => {
  switch (status) {
    case HealthCheckStatus.Pass:
      return RADIX.COLOR.SUCCESS;
    case HealthCheckStatus.Warn:
      return RADIX.COLOR.WARNING;
    case HealthCheckStatus.Fail:
      return RADIX.COLOR.DANGER;
    case HealthCheckStatus.Unknown:
    default:
      return RADIX.COLOR.NEUTRAL;
  }
};

const TestDetails = (props: { test: IHealthCheckSubmoduleCheckResult }) => {
  const { test } = props;

  const [showDetails, setShowDetails] = useState(false);
  const color = getTestColor(test.status);

  return (
    <Card>
      <Flex direction="column">
        <Text
          color={color}
          key={test.test_name}
          style={{
            userSelect: 'none',
            transition: 'font-weight 0.3s ease-in-out',
          }}
          onClick={() => setShowDetails((prev) => !prev)}
          className="cursor-pointer"
          weight={showDetails ? 'bold' : 'light'}
        >
          <Flex align="center" gap={RADIX.FLEX.GAP.XS} justify="between">
            {test.test_name}
            <ChevronUpIcon
              style={{
                transform: showDetails ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: 'transform 0.3s ease-in-out',
              }}
            />
          </Flex>
        </Text>
        <AnimateHeight
          duration={200}
          height={showDetails ? 'auto' : 0}
          easing="ease-in-out"
        >
          <Flex direction="column" mt="2">
            <Text wrap="wrap" color={RADIX.COLOR.SECONDARY} size="2">
              {test.test_description}
            </Text>
            <Separator size="4" my="4" />
            {test.checks.map((check) => (
              <React.Fragment key={check.check_type}>
                <DataList.Root>
                  <DataList.Item align="center">
                    <DataList.Label>
                      <Text weight="bold">Check</Text>
                    </DataList.Label>
                    <DataList.Value>
                      <Text weight="bold">{check.check_type}</Text>
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item align="center">
                    <DataList.Label>
                      <Text size="2">Status</Text>
                    </DataList.Label>
                    <DataList.Value>
                      {getResultBadge(check.status)}
                    </DataList.Value>
                  </DataList.Item>
                  <DataList.Item align="center">
                    <DataList.Label>
                      <Text size="2">Device</Text>
                    </DataList.Label>
                    <DataList.Value>{check.device || '--'}</DataList.Value>
                  </DataList.Item>
                  <DataList.Item align="start">
                    <DataList.Label>
                      <Text size="2">Message</Text>
                    </DataList.Label>
                    <DataList.Value>
                      <Text wrap="wrap">{check.message}</Text>
                    </DataList.Value>
                  </DataList.Item>
                  {check.event_id &&
                    check.status !== HealthCheckStatus.Pass && (
                      <DataList.Item align="center">
                        <DataList.Label>
                          <Text size="2">Event ID</Text>
                        </DataList.Label>
                        <DataList.Value>{check.event_id}</DataList.Value>
                      </DataList.Item>
                    )}
                </DataList.Root>
                <Separator size="4" my="4" />
              </React.Fragment>
            ))}
            {test.data && (
              <JsonViewer value={test.data} collapse={0} rootName="data" />
            )}
          </Flex>
        </AnimateHeight>
      </Flex>
    </Card>
  );
};

const getResultBadge = (status: HealthCheckStatus) => {
  const color = getTestColor(status);
  switch (status) {
    case HealthCheckStatus.Pass:
      return (
        <Badge color={color}>
          <Flex align="center" gap={RADIX.FLEX.GAP.XS}>
            <CheckIcon />
            <Text>{t('admin.passed')}</Text>
          </Flex>
        </Badge>
      );
    case HealthCheckStatus.Warn:
      return (
        <Badge color={color}>
          <Flex align="center" gap={RADIX.FLEX.GAP.XS}>
            <ExclamationTriangleIcon />
            <Text>{t('common.warning')}</Text>
          </Flex>
        </Badge>
      );
    case HealthCheckStatus.Fail:
      return (
        <Badge color={color}>
          <Flex align="center" gap={RADIX.FLEX.GAP.XS}>
            <Cross2Icon />
            <Text>{t('admin.failed')}</Text>
          </Flex>
        </Badge>
      );
    case HealthCheckStatus.Unknown:
      return (
        <Badge color={color}>
          <Flex align="center" gap={RADIX.FLEX.GAP.XS}>
            {/* This question mark icon is weirdly tall for some reason */}
            <QuestionMarkIcon height="12" />
            <Text>{t('constants.unknown')}</Text>
          </Flex>
        </Badge>
      );
    default:
      return null;
  }
};

interface ISubmoduleRow extends RowWithID, IHealthCheckSubmoduleCheckResponse {}

const getSubmoduleRows = (
  submoduleResults: IHealthCheckSubmoduleResults
): ISubmoduleRow[] =>
  Object.entries(submoduleResults).map(([key, value]) => ({
    _id: key,
    ...value,
  }));

interface IProps {
  showAllResults: boolean;
}

export const HealthCheckResultsTab = (props: IProps) => {
  const { showAllResults } = props;
  const { healthCheckResults } = useContext(MachineContext);

  const data = useMemo(() => {
    if (!healthCheckResults?.submodule_check_responses) {
      return [];
    }

    return getSubmoduleRows(healthCheckResults.submodule_check_responses);
  }, [healthCheckResults]);

  const columns = useMemo<ITypedTableColumn<ISubmoduleRow>[]>(
    () => [
      {
        label: 'common.submodule',
        key: '_id',
        formatFn: (m) => <Text>{startCase(m._id)}</Text>,
      },
      {
        label: 'common.status',
        key: 'status',
        formatFn: (m) => getResultBadge(m.status),
      },
      {
        label: showAllResults ? 'common.tests' : 'common.failed-tests',
        key: 'results',
        classNameFn: () => 'health-check-failed-tests-column',
        formatFn: (m) => {
          const results = showAllResults
            ? m.results
            : [
                ...m.results.filter((r) => r.status === HealthCheckStatus.Fail),
                ...m.results.filter((r) => r.status === HealthCheckStatus.Warn),
              ];

          return (
            <Flex direction="column" gap={RADIX.FLEX.GAP.XS} py="2">
              {results.length === 0 && '-'}
              {results.map((test) => (
                <TestDetails key={test.test_name} test={test} />
              ))}
            </Flex>
          );
        },
      },
    ],
    [showAllResults]
  );

  const numFailures = Object.keys(
    healthCheckResults?.failed_submodules_and_tests || {}
  ).length;

  return (
    <Flex direction="column" align="stretch" gap={RADIX.FLEX.GAP.MD}>
      {numFailures > 0 && (
        <CommonCallout
          color={RADIX.COLOR.DANGER}
          text={`${numFailures} submodule${
            numFailures > 1 ? 's' : ''
          } failed their health checks.`}
        />
      )}
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.MD}
        table={
          <CommonTableHoC
            dataTestID="PitchDetailsTab"
            identifier="PitchDetailsTabTable"
            displayColumns={columns}
            displayData={data}
          />
        }
      />
    </Flex>
  );
};
